import { createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'
import { placementEquals } from 'features/dashboard/operation/adGroupPlacementEdit/AdGroupPlacementEditModal'

const initialState = {
  visible: false,
  isSubmitting: false,
  previewVisible: false,
  isLoading: false,
  loadingError: undefined,
  adGroups: [],
  form: {
    placementType: 'ALL',
    placementCategories: [],
  },
  preview: {
    placementType: 'ALL',
    placementCategories: [],
  },
}

export const showAdGroupPlacementEdit = createAxiosThunk(
  'dashboard/showAdGroupPlacementEdit',
  adGroupIds => api.ad.getAdGroups(adGroupIds)
)

export const updateAdGroupsPlacement = createAxiosThunk(
  'dashboard/updateAdGroupsPlacement',
  (noArgs, { getState }) => {
    const {
      adGroups,
      form: { placementType, placementCategories },
    } = getState().dashboard.operation.adGroupPlacementEdit
    return api.ad.patchAdGroupsPlacement({
      adGroupIds: adGroups.map(({ id }) => id),
      placementType,
      ...(placementType === 'SETTING' && { placementCategories }),
    })
  }
)

const adGroupPlacementEditSlice = createSlice({
  name: 'dashboard/adGroupPlacementEdit',
  initialState,

  reducers: {
    hideAdGroupPlacementEdit: () => initialState,
    setPlacementType: (state, { payload }) => {
      if (state.form.placementType === 'ALL' && payload === 'SETTING') {
        state.form.placementCategories = [
          'PREMIUM_LINK_SEARCH_KAKAO',
          'PREMIUM_LINK_CONTENT_KAKAO',
          'PREMIUM_LINK_CONTENT_PARTNER',
        ]
      }
      state.form.placementType = payload
    },
    setPlacementCategory: (state, { payload: { checked, value } }) => {
      if (checked) {
        state.form.placementCategories.push(value)
      } else {
        const index = state.form.placementCategories.indexOf(value)
        if (index !== -1) state.form.placementCategories.splice(index, 1)
      }
    },
    setPlacementCategories: (state, { payload: { checked, values } }) => {
      if (checked) {
        const filtered = state.form.placementCategories.filter(
          item => !values.includes(item)
        )
        state.form.placementCategories = [...filtered, ...values]
      } else {
        state.form.placementCategories = state.form.placementCategories.filter(
          item => !values.includes(item)
        )
      }
    },
    showPreview: state => {
      state.preview.placementType = state.form.placementType
      state.preview.placementCategories = state.form.placementCategories
      state.previewVisible = true
    },
    hidePreview: state => {
      state.previewVisible = false
    },
  },

  extraReducers: builder => {
    builder.addCase(showAdGroupPlacementEdit.pending, state => {
      state.isLoading = true
      state.visible = true
    })

    builder.addCase(showAdGroupPlacementEdit.rejected, (state, { payload }) => {
      state.loadingError = payload
      state.isLoading = false
    })

    builder.addCase(
      showAdGroupPlacementEdit.fulfilled,
      (state, { payload: { adGroupDtos } }) => {
        const adGroups = adGroupDtos.map(({ id, name, placement }) => ({
          id,
          name,
          placement: {
            type: placement.type,
            categories: placement.categories
              ? placement.categories
                  .map(category => category.name)
                  .filter(
                    categoryName =>
                      categoryName !== 'PREMIUM_LINK_SEARCH_PARTNER'
                  )
              : [],
          },
        }))
        state.adGroups = adGroups
        const hasSameValue =
          adGroups.length > 1 &&
          adGroups.every((value, index, array) =>
            placementEquals(value.placement, array[0].placement)
          )
        if (adGroups.length === 1 || hasSameValue) {
          const [first] = adGroups
          const {
            placement: { type, categories },
          } = first
          state.form.placementType = type
          state.form.placementCategories = categories
        }
        state.isLoading = false
      }
    )

    builder.addCase(updateAdGroupsPlacement.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(updateAdGroupsPlacement.fulfilled, state => {
      state.isSubmitting = false
    })

    builder.addCase(updateAdGroupsPlacement.rejected, state => {
      state.isSubmitting = false
    })
  },
})
export const {
  hideAdGroupPlacementEdit,
  showPreview,
  hidePreview,
  setPlacementType,
  setPlacementCategory,
  setPlacementCategories,
} = adGroupPlacementEditSlice.actions
export default adGroupPlacementEditSlice.reducer
